import { pickerIds, type CartLineInput } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/** SAL-06's data layer. */

const KEY = ['pos'] as const;

export function useSession(registerId?: string) {
  return useQuery({
    queryKey: [...KEY, 'session', registerId],
    queryFn: async () => {
      const res = await api.pos.session({ query: registerId ? { registerId } : {} });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

/** A screenful of tiles at a counter, not a catalogue page. */
export const POS_PAGE_SIZE = 50;

/**
 * §5c's product grid, one page at a time (NFR-42).
 *
 * This read carried a hard `LIMIT 200` in SQL. At 50,000 items the cashier saw
 * the first 200 by name and nothing on screen said so — fast, and not correct.
 * The server states the match's TOTAL now, so the grid can say which it is.
 */
export function useProducts(
  registerId: string | undefined,
  search: string,
  page = 1,
  pageSize = POS_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...KEY, 'products', registerId, search, page, pageSize],
    enabled: Boolean(registerId),
    queryFn: async () => {
      const res = await api.pos.products({
        query: { registerId: registerId!, ...(search ? { search } : {}), page, pageSize },
      });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * §5i's sale list, ONE PAGE at a time (NFR-42) — measured at 3.5 s and 1.5 MB
 * over one shift of 2,000 sales.
 *
 * `refundable` is the rule the screen used to apply with `.filter()`: a refund
 * is not itself refundable. It is a server predicate now, because a LIMIT in
 * front of a JavaScript filter returns a short page rather than the right one.
 */
export function useShiftSales(shiftId: string | undefined, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...KEY, 'sales', shiftId, page, pageSize],
    enabled: Boolean(shiftId),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await api.pos.sales({
        params: { shiftId: shiftId! },
        query: { refundable: 'true', page, pageSize },
      });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

/**
 * The sale the cashier has TAPPED, resolved by id and by nothing else — the
 * picker convention (PAGINATION.md §7). Without it, paging the list away from a
 * selected sale blanks the refund pane, which is the truncated-`<select>`
 * defect arrived at from the other side.
 */
export function useHeldSale(shiftId: string | undefined, saleId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'sales', 'held', shiftId, saleId],
    enabled: Boolean(shiftId) && Boolean(saleId),
    queryFn: async () => {
      const res = await api.pos.sales({
        params: { shiftId: shiftId! },
        query: { ids: pickerIds([saleId!]), page: 1, pageSize: 1 },
      });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body.sales[0] ?? null;
    },
  });
}

export function useParkedCarts(shiftId: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'parked', shiftId],
    enabled: Boolean(shiftId),
    queryFn: async () => {
      const res = await api.pos.parkedCarts({ params: { shiftId: shiftId! } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body.carts;
    },
  });
}

/**
 * The escape from a dead end: a fresh company has no register, and SAL-06
 * describes no screen that makes one. See the contract note on the route.
 */
export function useCreateRegister() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { code: string; name: string; warehouseId: string }) => {
      const response = await api.pos.createRegister({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useOpenShift() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { registerId: string; openingCash: string }) => {
      const res = await api.pos.openShift({ body: input });
      if (res.status !== 201) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCloseShift(shiftId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { countedCash: string; discrepancyReason?: string }) => {
      const res = await api.pos.closeShift({ params: { shiftId }, body: input });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCompleteSale(shiftId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      lines: CartLineInput[];
      cartDiscount?: string;
      partnerId?: string;
      payments: { method: 'cash' | 'card'; amount: string; tendered?: string }[];
      parkedCartId?: string;
    }) => {
      const res = await api.pos.completeSale({ params: { shiftId }, body: input });
      if (res.status !== 201) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRefund(shiftId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      saleId: string;
      lines: { saleLineId: string; qty: string }[];
      restock: boolean;
      method: 'cash' | 'card';
    }) => {
      const res = await api.pos.refund({ params: { shiftId }, body: input });
      if (res.status !== 201) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useParkCart(shiftId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { label: string; lines: CartLineInput[]; partnerId?: string }) => {
      const res = await api.pos.parkCart({ params: { shiftId }, body: input });
      if (res.status !== 201) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUnpark() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (cartId: string) => {
      const res = await api.pos.deleteParkedCart({ params: { cartId } });
      if (res.status !== 204) throwApiError(res.status, res.body);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}
